import type { Direction } from '../sim/types';

/** InputManager is polled by the deterministic 60 Hz simulation. */

/**
 * Arcade default: a held direction repeats every tick, so the cat runs
 * continuously for as long as the key is down and stops the moment it is
 * released. A non-zero delay switches the same gate to deliberate single-step
 * taps, which remains available for accessibility tuning.
 */
export const DIRECTION_HOLD_DELAY_TICKS = 0;

/** Deliberate one-tile-per-press cadence, kept for the precise-step option. */
export const DIRECTION_STEP_HOLD_DELAY_TICKS = 18;

export interface DirectionCandidate {
  readonly id: string;
  readonly direction: Direction;
  readonly active: boolean;
  readonly priority: number;
}

export function pickLatestDirection(candidates: readonly DirectionCandidate[]): Direction | null {
  let latest: DirectionCandidate | null = null;
  for (const candidate of candidates) {
    if (!candidate.active) continue;
    if (!latest || candidate.priority > latest.priority) latest = candidate;
  }
  return latest?.direction ?? null;
}

/**
 * Emits the current direction intent for one simulation tick.
 *
 * With the default zero delay this is a pass-through: press and hold to keep
 * running, release to stop. With a positive delay the first press still pulses
 * immediately, then movement pauses until the key has been held that long,
 * which turns a tap into exactly one tile.
 */
export class DirectionRepeatGate {
  private activeDirection: Direction | null = null;
  private suppressedTicks = 0;

  constructor(private readonly holdDelayTicks = DIRECTION_HOLD_DELAY_TICKS) {
    if (!Number.isInteger(holdDelayTicks) || holdDelayTicks < 0) {
      throw new RangeError('Direction hold delay must be a non-negative integer.');
    }
  }

  next(direction: Direction | null): Direction | null {
    if (direction === null) {
      this.reset();
      return null;
    }

    if (direction !== this.activeDirection) {
      this.activeDirection = direction;
      this.suppressedTicks = 0;
      return direction;
    }

    if (this.suppressedTicks < this.holdDelayTicks) {
      this.suppressedTicks += 1;
      return null;
    }

    return direction;
  }

  reset(): void {
    this.activeDirection = null;
    this.suppressedTicks = 0;
  }
}
